import { useEffect, useState } from 'react';
import { useContext, useContextSelector } from 'use-context-selector';
import activeCustomerApi from 'src/api/ActiveCustomerApi';
import type { Customer } from 'src/types/customer';
import { Logger } from 'src/utils/logger';
import CustomerContext from '../contexts/CustomerContext';
import type { CustomerContextValue } from '../contexts/CustomerContext';

// Overload for when no selector is provided. Returns the entire context value.
function useCustomer(): CustomerContextValue;
// Overload for when a selector is provided. Returns only the selected slice of state.
function useCustomer<T>(selector: (state: CustomerContextValue) => T): T;
// Implementation of the overloaded function.
function useCustomer<T>(selector?: (state: CustomerContextValue) => T): T | CustomerContextValue {
  // If a selector function is passed, use useContextSelector for performance.
  if (selector) {
    return useContextSelector(CustomerContext, selector);
  }
  // Otherwise, return the entire context value for backward compatibility.
  return useContext(CustomerContext);
}

export const useCustomerByNumber = (customerNumber?: number): Customer | null => {
  const [customer, setCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    let isCurrent = true;

    if (!customerNumber) {
      setCustomer(null);
      return () => {
        isCurrent = false;
      };
    }

    setCustomer(null);
    activeCustomerApi
      .getCustomer(String(customerNumber))
      .then((result) => {
        if (isCurrent) {
          setCustomer(result);
        }
      })
      .catch((error) => {
        if (isCurrent) {
          setCustomer(null);
          Logger.error(`Error loading customer ${customerNumber}`, error);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [customerNumber]);

  return customer;
};

export default useCustomer;
